import { useCart } from '@shared/cart-react';
import { Product } from '@shared/react-product';

/**
 * useCartBreakdown handles the logic for the alarm panel and hub prices.
 *
 * Additional panels aren't a separate product in datoCMS. Design often shows
 * the first panel and the rest separately in the cart breakdown. Because of this,
 * we need to separate the prices and display them correctly.
 *
 * Along with separating the prices, we also need to make a mock product for
 * the additional panels so that we can display them separately from the initial
 * panel.
 *
 * @public
 */
const useCartBreakdown = () => {
  const cart = useCart();
  const { cartProducts, products, monitoringPlan } = cart;
  const cartTotals = cart?.totals?.totals;
  let panelHubPrice = 0;
  let cartProductList: CartProductType[] = [];
  let productList: ProductType[] = [];
  let totals: TotalsType = {
    total: { salePrice: 0, price: 0 },
    monitoring: { salePrice: 0, price: 0 },
    equipment: { salePrice: 0, price: 0 },
  };

  const hubPrice = parseFloat(
    cart?.calculateProductDiscounts?.('CPA-HUB-345-1')?.salePrice ?? `${0}`
  );
  const panelPrice = parseFloat(
    cart?.calculateProductDiscounts?.('CPA-PANEL-345-1')?.salePrice ?? `${0}`
  );

  const getMonthlyMonitoring = () => {
    let finalPrice = 0;
    let plan = monitoringPlan?.sku ?? 'vm-pn';

    if (!!plan) {
      finalPrice = products?.[plan]?.price || 0;
    }

    return finalPrice;
  };

  if (cartProducts) {
    panelHubPrice = hubPrice + panelPrice;

    cartProductList = [
      ...Object.values(cartProducts || {}).filter(
        ({ sku, productType }) =>
          !baseProducts.includes(sku) &&
          !monitoringPlans.includes(sku) &&
          productType !== 'package'
      ),
      {
        sku: 'CPA-PANEL-345-1',
        total:
          panelPrice -
          panelPrice / (cartProducts?.['CPA-PANEL-345-1']?.qty || 1),
        name: 'Additional Alarm Panel',
        maximumSensorCountOnSite: 3,
        qty: (cartProducts?.['CPA-PANEL-345-1']?.qty || 1) - 1,
        position: 23,
      },
    ]
      .filter(product => !!product)
      .map(product => ({
        position: cart?.products?.[product?.sku || '']?.position ?? -1,
        ...product,
      }))
      .sort((a, b) => a.position - b.position);

    if (products) {
      productList = [
        ...breakdownProducts.map(sku => {
          const cartProduct = cartProducts[sku];
          const product = products[sku];
          return { ...product, qty: 0, ...cartProduct };
        }),
        {
          sku: 'CPA-PANEL-345-1',
          salePrice: panelPrice,
          name: 'Additional Alarm Panel',
          maximumSensorCountOnSite: 3,
          qty: (cartProducts?.['CPA-PANEL-345-1']?.qty || 1) - 1,
          position: 23,
        },
      ];
    }
  }

  if (cartTotals) {
    totals = {
      monitoring: {
        salePrice: parseFloat(cartTotals.monitoringTotalWithTax),
        price: parseFloat(cartTotals.monitoringBaseTotal),
      },
      equipment: {
        salePrice: parseFloat(cartTotals.equipmentTotalWithTax),
        price: parseFloat(cartTotals.equipmentBaseTotal),
      },
      total: {
        salePrice: parseFloat(cartTotals.orderTotalWithTax),
        price: parseFloat(cartTotals.orderBaseTotal),
      },
    };
  }

  return {
    cartProductList,
    panelHubPrice,
    productList,
    totals,
    monthlyMonitoring: getMonthlyMonitoring(),
  };
};

const baseProducts = [
  'CPA-HUB-345-1',
  'CPA-PANEL-345-1',
  'CP-YS-23i-1',
  'window-sticker-sm',
];

const breakdownProducts = [
  'CP-DS-345-1',
  'CP-WS-345-1',
  'CP-PIR-345-1',
  '2G-GB-345-1',
  'indoor-camera-eufy',
  'outdoor-camera-eufy',
  'doorbell-camera-eufy',
  'CP-FS-345-1',
  '2GIG-CO8-345',
  '2GIG-SMKT8-345',
  'CP-MED-345-1',
  'CP-KEY-345-1',
  'secondary-siren',
];

const monitoringPlans = ['bm-pl', 'bm-pn', 'vm-pl', 'vm-pn'];

type CartProductType = {
  name: string;
  sku: string;
  qty: number;
  baseQty: number;
  discountPrice: number;
  retailTotal: number;
  baseTotal: number;
  baseTotalWithTax: number;
  total: number;
  grandTotal: number;
  appliedDiscounts: string[];
  taxAmount: number;
  discountAmount: number;
  coveCraditApplied: number;
  pandoId: number;
  payLaterEligible: boolean;
  price: number;
  productType: string;
};

type ProductType = Product & CartProductType;

type Total = {
  salePrice: number;
  price: number;
};

type TotalsType = {
  [key: string]: Total;
};

export default useCartBreakdown;
